// frontend/src/pages/About.tsx

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Shield, 
  Lock, 
  TrendingUp, 
  ArrowRight,
  Bitcoin,
  Network,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';

export function About() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-apple-blue-50 to-white py-16 lg:py-24">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              How SLE DEX Works
            </h1>
            <p className="text-xl text-apple-gray-600 mb-8">
              A peer-to-peer Bitcoin exchange powered by Lightning Network and Taproot Assets
            </p>
          </div>
        </div>
      </section>

      {/* What is SLE DEX */}
      <section className="py-16 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              What is SLE DEX?
            </h2>
            <div className="prose prose-lg max-w-none text-apple-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                SLE DEX is a decentralized exchange (DEX) that enables peer-to-peer trading of Bitcoin 
                and stablecoins using cutting-edge Lightning Network technology and Taproot Assets. 
                Unlike traditional exchanges, SLE DEX gives you complete control over your funds while 
                providing instant settlements and lightning-fast transactions.
              </p>
              <p className="text-lg leading-relaxed">
                Our platform uses atomic swaps powered by Hash Time-Locked Contracts (HTLCs) to ensure 
                trustless trading - meaning trades either complete fully or don't happen at all, with 
                no possibility of one party stealing funds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Step by Step */}
      <section className="py-16 bg-apple-gray-50">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              How Trading Works
            </h2>
            
            <div className="space-y-8">
              {/* Step 1 */}
              <Card className="border-l-4 border-apple-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-apple-blue-500 text-white flex items-center justify-center text-xl font-bold">
                        1
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">Create an Order</h3>
                      <p className="text-apple-gray-700 leading-relaxed">
                        Place a buy or sell order on the platform. Specify the asset you want to trade 
                        (USDC, USDT, or DAI), the amount of Bitcoin, and whether you're buying or selling. 
                        Your order joins the order book where other traders can match with it.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 2 */}
              <Card className="border-l-4 border-apple-green-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-apple-green-500 text-white flex items-center justify-center text-xl font-bold">
                        2
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">Order Matching</h3>
                      <p className="text-apple-gray-700 leading-relaxed">
                        When another trader wants to take the opposite side of your trade, the platform 
                        automatically matches your orders. This happens instantly, and both parties are 
                        notified that a match has been found.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 3 */}
              <Card className="border-l-4 border-apple-orange-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-apple-orange-500 text-white flex items-center justify-center text-xl font-bold">
                        3
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">Atomic Swap Execution</h3>
                      <p className="text-apple-gray-700 leading-relaxed">
                        The trade executes using Hash Time-Locked Contracts (HTLCs) on the Lightning Network. 
                        This ensures the swap is atomic - both parties either receive their assets simultaneously, 
                        or the trade doesn't happen. There's no counterparty risk or need to trust the exchange.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 4 */}
              <Card className="border-l-4 border-apple-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-apple-blue-500 text-white flex items-center justify-center text-xl font-bold">
                        4
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">Instant Settlement</h3>
                      <p className="text-apple-gray-700 leading-relaxed">
                        The assets are transferred instantly via the Lightning Network. Bitcoin is sent as 
                        Lightning payments, while stablecoins are transferred as Taproot Assets. Settlement 
                        happens in seconds, not minutes or hours like traditional exchanges.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Technologies */}
      {/* Dynamic Spread Automation */}
      <section className="py-16 bg-gradient-to-br from-apple-green-50 to-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Dynamic Spread Automation (DSA)
            </h2>
            
            <Card className="bg-white border-apple-green-200 mb-8">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <TrendingUp className="w-12 h-12 text-apple-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">What is DSA?</h3>
                    <p className="text-apple-gray-700 leading-relaxed mb-4">
                      Dynamic Spread Automation is SLE DEX's innovative price protection mechanism that 
                      automatically adjusts maker order prices based on Bitcoin's real-time market volatility. 
                      Unlike traditional exchanges where your orders remain static, DSA ensures your orders 
                      "slide" with BTC price movements.
                    </p>
                    <p className="text-apple-gray-700 leading-relaxed">
                      This means makers are protected from sudden price swings while maintaining competitive 
                      spreads, and takers always get fair market prices. It's win-win for everyone.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold mb-6">How DSA Works: Step-by-Step</h3>
            
            <div className="space-y-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-apple-green-500 text-white flex items-center justify-center font-bold">
                        1
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Maker Creates Order with Spread</h4>
                      <p className="text-apple-gray-700">
                        When you create a sell order, you set your desired spread percentage (e.g., 2%). 
                        This is your profit margin above the current BTC price. For example, if BTC is at 
                        $96,000 and you set a 2% spread, your order price is $97,920.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-apple-green-500 text-white flex items-center justify-center font-bold">
                        2
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Real-Time Price Monitoring</h4>
                      <p className="text-apple-gray-700">
                        SLE DEX continuously fetches Bitcoin prices from CoinGecko API every 30 seconds. 
                        The DSA engine monitors these price changes and calculates volatility in real-time. 
                        No manual intervention needed - it's completely automated.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-apple-green-500 text-white flex items-center justify-center font-bold">
                        3
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Automatic Price Adjustment</h4>
                      <p className="text-apple-gray-700 mb-3">
                        When BTC price moves significantly (&gt;0.5%), DSA automatically recalculates your order 
                        price to maintain your spread. If BTC rises to $97,000, your order automatically 
                        adjusts to $98,940 (still 2% spread). If BTC drops to $95,000, your order drops to 
                        $96,900.
                      </p>
                      <p className="text-apple-gray-700">
                        <strong>Volatility Protection:</strong> During high volatility, DSA can widen spreads 
                        automatically (within your limits) to protect you from rapid price swings.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-apple-green-500 text-white flex items-center justify-center font-bold">
                        4
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Orders Stay Competitive</h4>
                      <p className="text-apple-gray-700">
                        Your orders remain at the top of the order book with competitive pricing. Takers see 
                        fair market prices that reflect current BTC value. Everyone wins: makers are protected, 
                        takers get good prices, and the market stays liquid.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold mb-6">DSA Example: Real Numbers</h3>
            
            <Card className="bg-apple-green-50 border-apple-green-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-apple-gray-600 mb-1">Initial Setup</div>
                      <div className="text-lg font-semibold">BTC: $96,000</div>
                      <div className="text-sm text-apple-green-600">Spread: 2%</div>
                      <div className="text-xl font-bold text-apple-green-700 mt-2">Order: $97,920</div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-apple-gray-600 mb-1">BTC Rises (+$1,000)</div>
                      <div className="text-lg font-semibold">BTC: $97,000</div>
                      <div className="text-sm text-apple-green-600">Spread: 2%</div>
                      <div className="text-xl font-bold text-apple-green-700 mt-2">Order: $98,940</div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-apple-gray-600 mb-1">BTC Drops (-$1,000)</div>
                      <div className="text-lg font-semibold">BTC: $95,000</div>
                      <div className="text-sm text-apple-green-600">Spread: 2%</div>
                      <div className="text-xl font-bold text-apple-green-700 mt-2">Order: $96,900</div>
                    </div>
                  </div>
                  
                  <p className="text-apple-gray-700 text-center mt-4">
                    <strong>Result:</strong> Your 2% profit margin is maintained regardless of BTC price movement. 
                    No manual adjustments needed!
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h4 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-apple-green-500" />
                    Benefits for Makers
                  </h4>
                  <ul className="space-y-2 text-apple-gray-700">
                    <li>• Protected from volatility losses</li>
                    <li>• Maintain consistent profit margins</li>
                    <li>• No manual price monitoring needed</li>
                    <li>• Orders stay competitive automatically</li>
                    <li>• Set min/max spread limits for control</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-apple-green-500" />
                    Benefits for Takers
                  </h4>
                  <ul className="space-y-2 text-apple-gray-700">
                    <li>• Always get current market prices</li>
                    <li>• Fair spreads reflect true BTC value</li>
                    <li>• More liquid order book</li>
                    <li>• Better execution prices</li>
                    <li>• Transparent pricing at all times</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Key Technologies
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Lightning Network */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-8 h-8 text-apple-blue-500" />
                    <h3 className="text-2xl font-semibold">Lightning Network</h3>
                  </div>
                  <p className="text-apple-gray-700 leading-relaxed">
                    A Layer 2 payment protocol built on top of Bitcoin that enables instant, low-cost 
                    transactions. Lightning Network allows SLE DEX to process thousands of trades per 
                    second with minimal fees.
                  </p>
                </CardContent>
              </Card>

              {/* Taproot Assets */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Network className="w-8 h-8 text-apple-green-500" />
                    <h3 className="text-2xl font-semibold">Taproot Assets</h3>
                  </div>
                  <p className="text-apple-gray-700 leading-relaxed">
                    A protocol that enables the creation and transfer of assets on Bitcoin's blockchain. 
                    Stablecoins like USDC and USDT are issued as Taproot Assets, allowing them to be 
                    traded on Lightning Network with Bitcoin-level security.
                  </p>
                </CardContent>
              </Card>

              {/* HTLCs */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-8 h-8 text-apple-orange-500" />
                    <h3 className="text-2xl font-semibold">Hash Time-Locked Contracts</h3>
                  </div>
                  <p className="text-apple-gray-700 leading-relaxed">
                    Smart contracts that ensure atomic swaps. HTLCs use cryptographic hashes and time locks 
                    to guarantee that both parties fulfill their obligations or the trade automatically 
                    reverts, preventing theft or fraud.
                  </p>
                </CardContent>
              </Card>

              {/* Non-Custodial */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-8 h-8 text-apple-blue-500" />
                    <h3 className="text-2xl font-semibold">Non-Custodial Architecture</h3>
                  </div>
                  <p className="text-apple-gray-700 leading-relaxed">
                    You maintain full control of your private keys and funds at all times. SLE DEX never 
                    holds your assets - all trades happen directly between users through peer-to-peer 
                    Lightning channels.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-apple-gray-50">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Why Choose SLE DEX?
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">True Decentralization</h3>
                  <p className="text-apple-gray-700">
                    No central authority controls the exchange. Trades happen directly between users 
                    without intermediaries.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
                  <p className="text-apple-gray-700">
                    Settlements happen in seconds, not hours. Lightning Network enables instant finality 
                    with minimal fees.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Your Keys, Your Crypto</h3>
                  <p className="text-apple-gray-700">
                    Complete custody of your funds. We never hold your Bitcoin or stablecoins - you 
                    maintain full control at all times.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Transparent Pricing</h3>
                  <p className="text-apple-gray-700">
                    Only 0.5-1% platform fee with no hidden charges. All fees are clearly displayed 
                    before you trade.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Trustless Trading</h3>
                  <p className="text-apple-gray-700">
                    Atomic swaps eliminate counterparty risk. Trades either complete fully or don't 
                    happen - no risk of partial execution or theft.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-apple-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Bitcoin Security</h3>
                  <p className="text-apple-gray-700">
                    All assets benefit from Bitcoin's security model. Taproot Assets inherit the same 
                    level of security as Bitcoin itself.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-16 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Security & Trust
            </h2>

            <Card className="bg-apple-blue-50 border-apple-blue-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Shield className="w-12 h-12 text-apple-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-semibold mb-3">How We Keep Your Funds Safe</h3>
                    <div className="space-y-4 text-apple-gray-700">
                      <p className="leading-relaxed">
                        <strong>Non-Custodial Design:</strong> We never take custody of your funds. 
                        All Bitcoin and Taproot Assets remain in your Lightning channels under your 
                        control at all times.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Atomic Swaps:</strong> HTLCs ensure that trades are atomic - both 
                        parties receive their assets simultaneously or the trade doesn't execute. 
                        There's no window for theft or partial completion.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Open Source:</strong> Our smart contracts and core protocols are 
                        open source and auditable by anyone. Transparency is key to building trust.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Lightning Network Security:</strong> Lightning Network is battle-tested 
                        technology processing billions of dollars in transactions. Your trades benefit 
                        from this proven security model.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Risks & Considerations */}
      <section className="py-16 bg-apple-gray-50">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Important Considerations
            </h2>

            <Card className="bg-apple-yellow-50 border-apple-yellow-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <AlertCircle className="w-12 h-12 text-apple-yellow-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-semibold mb-3">What You Should Know</h3>
                    <div className="space-y-4 text-apple-gray-700">
                      <p className="leading-relaxed">
                        <strong>Lightning Network Requirements:</strong> You'll need a Lightning wallet 
                        and some Bitcoin to open channels. Initial channel setup requires on-chain 
                        transactions which have network fees.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Channel Liquidity:</strong> Lightning channels have limited capacity. 
                        Large trades may require multiple channels or additional liquidity.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Network Fees:</strong> While Lightning fees are minimal, opening and 
                        closing channels requires on-chain Bitcoin transactions that incur mining fees.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Beta Software:</strong> SLE DEX and Taproot Assets are cutting-edge 
                        technology. While extensively tested, users should start with small amounts 
                        and understand the risks involved.
                      </p>
                      <p className="leading-relaxed">
                        <strong>Regulatory Status:</strong> Cryptocurrency regulations vary by 
                        jurisdiction. Users are responsible for ensuring compliance with their local laws.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-apple-blue-500 to-apple-blue-600 text-white">
        <div className="container-main text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience the Future of Trading?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join SLE DEX today and start trading Bitcoin and stablecoins with Lightning speed, 
            complete security, and full control of your assets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button
                variant="secondary"
                size="lg"
                className="bg-white text-apple-blue-600 hover:bg-apple-gray-100"
                icon={<ArrowRight className="w-5 h-5" />}
              >
                Create Free Account
              </Button>
            </Link>
            <Link to="/trade">
              <Button
                variant="secondary"
                size="lg"
                className="bg-apple-blue-700 text-white hover:bg-apple-blue-800 border-2 border-white"
              >
                Start Trading
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
